import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DefaultValues } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { SectionTabs, withCount } from '~/components/common/SectionTabs';
import { daysUntil } from '~/components/contractReview/contractDetailShared';
import { ContractPreviewPanel } from '~/components/contractReview/ContractPreviewPanel';
import { getContractsTableColumns } from '~/components/contractReview/contractTableColumns';
import { mapContract } from '~/components/contractReview/mapContract';
import type { ContractReview } from '~/components/contractReview/types';
import { ContractFormDialog } from '~/components/contracts/form/ContractFormDialog';
import { ContractForm } from '~/components/contracts/types';
import UploadContractDialog from '~/components/contracts/UploadContractDialog';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import { useContractsForProperty } from '~/data/hooks/useContracts';
import { type Attachment } from '~/data/schemas/attachment';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_SX } from '~/theme';
import { canAccess, EDIT_CONTRACT } from '~/utils/role';
import { buildUrlForContract } from '~/utils/urlBuilder';

export type PropertyContractsProps = {
  propertyId: string;
};

type AddContractDialogState =
  | { state: 'closed' }
  | {
      state: 'uploading-contract-attachments';
      defaultValues: DefaultValues<ContractForm> | undefined;
    }
  | {
      state: 'filling-out-contract-details';
      defaultValues: DefaultValues<ContractForm>;
    };

const defaultAddContractDialogState: Readonly<AddContractDialogState> = { state: 'closed' };

/** A contract at this property, with how many of its elevators are on it — the contract-wide car count would be the wrong number on a page about one building. */
type PropertyContractRow = ContractReview & { carsHere: number };

/**
 * The contracts covering this property, live ones and lapsed ones, in the Contracts
 * page's own column set. A row opens the contract page, which is where everything
 * about it lives; adding one stays here, since a contract is uploaded against the
 * property it covers.
 */
export const PropertyContracts: React.FC<PropertyContractsProps> = ({ propertyId }) => {
  const [searchParams] = useSearchParams();
  const terms = useLocaleTerms();
  const {
    userDetails: { role: userRole },
    currencyCode,
  } = useContext(UserContext);
  const canEdit = canAccess(userRole, EDIT_CONTRACT);

  const { contracts, isLoading } = useContractsForProperty(propertyId);
  const [addDialogState, setAddDialogState] = useState<AddContractDialogState>(
    defaultAddContractDialogState,
  );

  const rows = useMemo<PropertyContractRow[]>(
    () =>
      contracts.map((contract) => {
        const review = mapContract(contract);
        return {
          ...review,
          carsHere: review.properties.find((property) => property.id === propertyId)?.carCount ?? 0,
        };
      }),
    [contracts, propertyId],
  );

  // A contract whose expiration has passed is closed, and so is one with no expiration
  // recorded — `daysUntil` returns null for a missing or unparseable date, not NaN.
  const { active, closed } = useMemo(() => {
    const withStanding = rows.map((row) => ({
      row,
      remaining: daysUntil(row.expirationDate),
    }));
    return {
      active: withStanding
        .filter((r) => r.remaining !== null && r.remaining >= 0)
        .map((r) => r.row),
      closed: withStanding.filter((r) => r.remaining === null || r.remaining < 0).map((r) => r.row),
    };
  }, [rows]);

  const columns = useMemo<LiftAIGridColDef<PropertyContractRow>[]>(() => {
    const base = getContractsTableColumns<PropertyContractRow>({
      currencyCode,
      vehiclesLabel: terms.vehicles,
    }).filter(
      // The property is fixed — this one — so the properties roll-up says nothing, and the
      // contract-wide car count is the wrong number beside it.
      (column) => column.field !== 'properties' && column.field !== 'carCount',
    );

    const here: LiftAIGridColDef<PropertyContractRow> = {
      field: 'carsHere',
      headerName: terms.elevators,
      type: 'number',
      width: 110,
      align: 'right',
      headerAlign: 'right',
    };

    const vendorIndex = base.findIndex((column) => column.field === 'vendor');
    base.splice(vendorIndex + 1, 0, here);
    return base;
  }, [terms.elevators, terms.vehicles, currencyCode]);

  // A contract has a page of its own, so a row is a link and leaves this property
  // where it was.
  const rowHref = useCallback(
    (row: PropertyContractRow) => buildUrlForContract(String(row.id), searchParams),
    [searchParams],
  );

  const openUpload = () => {
    setAddDialogState({ state: 'uploading-contract-attachments', defaultValues: undefined });
  };

  const handleFilesUploaded = (attachments: Attachment[]) => {
    setAddDialogState({ state: 'uploading-contract-attachments', defaultValues: { attachments } });
  };

  const onUploadDocumentsNext = (attachments: Attachment[]) => {
    setAddDialogState({
      state: 'filling-out-contract-details',
      defaultValues: {
        action: 'create',
        attachments: attachments,
        fireService: null,
        pressureTest: null,
        phoneMonitoring: null,
        propertyContract: {
          propertyId,
          cars: [],
          rateSetId: null,
        },
        rateSets: [],
        reviewRules: [],
        customDetails: [],
      },
    });
  };

  const dialogs = (
    <>
      {addDialogState.state === 'uploading-contract-attachments' ? (
        <UploadContractDialog
          open
          onClose={() => setAddDialogState(defaultAddContractDialogState)}
          onFilesUploaded={handleFilesUploaded}
          onNext={onUploadDocumentsNext}
        />
      ) : null}

      {addDialogState.state === 'filling-out-contract-details' ? (
        <ContractFormDialog
          type="add"
          defaultValues={addDialogState.defaultValues}
          open
          onClose={() => setAddDialogState(defaultAddContractDialogState)}
          propertyId={propertyId}
        />
      ) : null}
    </>
  );

  // An empty grid would say "No rows" where the reason matters — and where the thing to
  // do about it is the upload this tab owns.
  if (!isLoading && rows.length === 0) {
    return (
      <Box sx={{ p: 3, width: '100%' }}>
        <Alert
          severity="info"
          action={
            canEdit ? (
              <Button size="small" onClick={openUpload}>
                Add contract
              </Button>
            ) : null
          }
        >
          <Stack>
            <Typography variant="body2">No contract covers this property.</Typography>
            <Typography variant="caption" color="text.secondary">
              Upload the signed contract and LiftAI pulls out its terms, pricing and rates — which
              is what AI invoice review checks each bill against.
            </Typography>
          </Stack>
        </Alert>
        {dialogs}
      </Box>
    );
  }

  const view = (contracts: PropertyContractRow[], emptyMessage: string) =>
    !isLoading && contracts.length === 0 ? (
      <Alert severity="info">{emptyMessage}</Alert>
    ) : (
      <ContractsTable rows={contracts} columns={columns} loading={isLoading} rowHref={rowHref} />
    );

  // On the tab row rather than above the grid: adding a contract is the section's action,
  // not the selected view's, and it reads the same on Active and Closed.
  const addContract = canEdit ? (
    <Button variant="contained" startIcon={<AddIcon />} onClick={openUpload} sx={BUTTON_SX}>
      Add contract
    </Button>
  ) : null;

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <SectionTabs
        ariaLabel="Contract views"
        actions={addContract}
        tabs={[
          {
            value: 'active',
            label: withCount('Active', isLoading ? undefined : active.length),
            render: () => view(active, 'No active contract covers this property.'),
          },
          {
            value: 'closed',
            label: withCount('Closed', isLoading ? undefined : closed.length),
            render: () =>
              view(closed, 'No contract on this property has lapsed or is missing an end date.'),
          },
        ]}
      />
      {dialogs}
    </Box>
  );
};

/**
 * Its own component so each subtab's grid keeps its own `apiRef` — sorting the closed
 * list would otherwise reach into the active one, and `useGridApiRef` cannot be called
 * from inside the `render` callback that builds them.
 */
function ContractsTable({
  rows,
  columns,
  loading,
  rowHref,
}: {
  rows: PropertyContractRow[];
  columns: LiftAIGridColDef<PropertyContractRow>[];
  loading: boolean;
  rowHref?: (row: PropertyContractRow) => string;
}) {
  const apiRef = useGridApiRef();
  const tableHeight = useDetailTableHeight();
  const preview = useTableRowPreview({ list: 'property_contracts', apiRef });

  return (
    <>
      <LiftAITable<PropertyContractRow>
        height={tableHeight}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        rowCount={rows.length}
        loading={loading}
        getRowId={(row) => row.id}
        tableHeaderTitle="Contracts"
        disableRowSelectionOnClick
        rowHref={rowHref}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
      />
      {preview.enabled ? <ContractPreviewPanel rowPreview={preview.rowPreview} /> : null}
    </>
  );
}
